import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { finalize } from 'rxjs';
import _ from 'lodash';
import { EmployeeAdvance, EmployeeAdvanceStatus, EmployeeAdvanceUpdate } from '../../../models/employee-advance/employee-advance';
import { EmployeeAdvanceFormModel, employeeAdvanceFieldLabels } from '../../../models/employee-advance/employee-advance-form';
import { ExpenseReceiptOwner } from '../../../models/expense-receipt/expense-receipt';
import { FundingBankAccount } from '../../../models/fund-movement/fund-movement';
import { PettyCashRoles } from '../../../models/roles/roles-group';
import { EmployeeAdvanceProxyService } from '../../../proxies/employee-advance.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { currencyLabel, currencySymbol } from '../../../utilities/currency.util';
import { reportInvalidFormFields } from '../../../utilities/form-error-reporter.util';
import { ControlsOf } from '../../../utilities/form.types';
import { BankChargesOption } from '../../shared/bank-charges-section/bank-charges-section.component';
import { cashDocumentStatusLabel, cashDocumentStatusSeverity } from '../cash-document-status.util';

const LIST_ROUTE = '/employee-advances';
const NEW_ID = 'new';

/**
 * One employee advance (Anticipo de viaje, #5789). The treasurer picks an Open trip — the draft is created for it, so
 * the employee and currency are known — then enters the amount, the bank and its charges, and posts it: Dr the
 * employee's advance account / Cr bank, and the bank movement. The model holds several trips; this page handles one.
 */
@Component({
  selector: 'app-employee-advance-details',
  templateUrl: './employee-advance-details.component.html',
})
export class EmployeeAdvanceDetailsComponent implements OnInit {
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly proxy = inject(EmployeeAdvanceProxyService);
  private readonly userAuth = inject(UserAuthenticationService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly canPay = this.userAuth.roles.includes(PettyCashRoles.PettyCashTreasurer);
  readonly fieldLabels = employeeAdvanceFieldLabels;

  readonly isLoading = signal(0);
  readonly isNew = signal(true);
  readonly currentId = signal<string | null>(null);
  readonly advance = signal<EmployeeAdvance | null>(null);
  readonly trips = signal<ExpenseReceiptOwner[]>([]);
  readonly banks = signal<FundingBankAccount[]>([]);

  /** New mode: the trip the advance is for. */
  readonly tripControl = new FormControl<string | null>(null, Validators.required);

  readonly form = new FormGroup<ControlsOf<EmployeeAdvanceFormModel>>({
    amount: new FormControl(0, { nonNullable: true, validators: [Validators.required, Validators.min(0.01)] }),
    advanceDate: new FormControl<Date | null>(new Date(), Validators.required),
    bankAccount: new FormControl<string | null>(null),
    bankingReference: new FormControl<string | null>(null),
    bankingFee: new FormControl<number | null>(0, Validators.min(0)),
    securityFee: new FormControl<number | null>(0, Validators.min(0)),
    note: new FormControl<string | null>(null),
  });

  /** Mirrors the form for the computed summary, including loads made with emitEvent: false. */
  private readonly formValue = signal(this.form.getRawValue());

  readonly saveMenuItems: MenuItem[] = [
    { label: this.label('Save and Close', 'Save and Close'), icon: 'pi pi-save', command: () => this.save(true) },
  ];

  readonly status = computed(() => this.advance()?.status ?? EmployeeAdvanceStatus.Draft);
  readonly isPosted = computed(() => this.status() === EmployeeAdvanceStatus.Posted);
  readonly isEditable = computed(() => this.canPay && !this.isNew() && !this.isPosted());
  readonly line = computed(() => this.advance()?.lines[0] ?? null);
  readonly headerTitle = computed(() =>
    this.isNew() ? this.label('EmployeeAdvance_New_Title', 'New advance') : (this.advance()?.number ?? ''),
  );
  readonly tripOptions = computed(() => this.trips().map((t) => ({ label: `${t.owner.number} · ${t.name}`, value: t.owner.reference })));
  readonly bankOptions = computed<BankChargesOption[]>(() =>
    this.banks().map((bank) => ({
      label: [bank.code, bank.bankName, bank.name, `****${bank.bankAccountNumber.slice(-4)}`].filter((s) => !!s).join(' · '),
      value: bank.reference,
    })),
  );
  readonly totalCashOut = computed(() => {
    const value = this.formValue();
    return Math.round((value.amount + (value.bankingFee ?? 0) + (value.securityFee ?? 0)) * 100) / 100;
  });

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.formValue.set(this.form.getRawValue()));
  }

  ngOnInit(): void {
    const query = this.route.snapshot.queryParamMap;
    const id = query.get('id');
    if (_.isNil(id)) {
      this.back();
      return;
    }
    this.isNew.set(id === NEW_ID);
    if (this.isNew()) {
      this.breadcrumbService.setCurrent('Common_New');
      const tripReference = query.get('tripReference');
      if (!_.isNil(tripReference)) this.tripControl.setValue(tripReference);
      this.loadTrips();
    } else {
      this.currentId.set(id);
      this.loadItem(id);
    }
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private loadTrips(): void {
    this.startLoading();
    this.proxy
      .getTrips()
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => this.trips.set(response.data ?? []));
  }

  private loadItem(id: string): void {
    this.startLoading();
    this.proxy
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        if (_.isNil(response.data)) return;
        this.setAdvance(response.data);
        this.loadBanks(id);
      });
  }

  private loadBanks(id: string): void {
    this.proxy.getBankAccounts(id).subscribe((response) => this.banks.set(response.data ?? []));
  }

  private setAdvance(advance: EmployeeAdvance): void {
    this.advance.set(advance);
    this.breadcrumbService.setCurrent(advance.number);
    this.form.patchValue(
      {
        amount: advance.lines[0]?.amount ?? advance.amount,
        advanceDate: this.fromApiDate(advance.advanceDate),
        bankAccount: advance.bankAccountReference,
        bankingReference: advance.bankingReference,
        bankingFee: advance.transferFee,
        securityFee: advance.securityTax,
        note: advance.note,
      },
      { emitEvent: false },
    );
    if (this.isEditable()) {
      this.form.enable({ emitEvent: false });
    } else {
      this.form.disable({ emitEvent: false });
    }
    this.form.markAsPristine();
    this.formValue.set(this.form.getRawValue());
  }

  /** New mode: the draft for the chosen trip; the amount comes next. */
  create(): void {
    if (this.tripControl.invalid || _.isNil(this.tripControl.value)) {
      this.tripControl.markAsTouched();
      GlobalMessageUtil.warn(this.title(), this.label('EmployeeAdvance_Trip_Required', 'Choose the trip the advance is for.'));
      return;
    }
    this.startLoading();
    this.proxy
      .create({ tripReferences: [this.tripControl.value] })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const created = response.data;
        if (_.isNil(created)) return;
        this.isNew.set(false);
        this.currentId.set(created.id);
        this.router.navigate([], { queryParams: { id: created.id }, replaceUrl: true });
        this.setAdvance(created);
        this.loadBanks(created.id);
      });
  }

  save(shouldClose = false): void {
    if (this.isNew()) {
      this.create();
      return;
    }
    if (!this.validate(false)) return;
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.startLoading();
    this.proxy
      .update(id, this.payload())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        if (_.isNil(response.data)) return;
        GlobalMessageUtil.success(this.title(), this.label('EmployeeAdvance_Saved', 'Advance saved.'));
        if (shouldClose) {
          this.back();
          return;
        }
        this.setAdvance(response.data);
      });
  }

  /** Saves first so the posted figures are exactly what is on screen, then posts. */
  confirmPost(): void {
    if (!this.validate(true)) return;
    const advance = this.advance();
    if (_.isNil(advance)) return;
    this.confirmationService.confirm({
      header: this.label('EmployeeAdvance_Post_Title', 'Post advance'),
      message: this.label(
        'EmployeeAdvance_Post_Message',
        'Post {amount}? This books the entry and the bank movement. It cannot be undone here.',
      ).replace('{amount}', `${currencySymbol(advance.currency)} ${this.totalCashOut().toFixed(2)}`),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.label('EmployeeAdvance_Post', 'Post'),
      rejectLabel: this.label('Cancel', 'Cancel'),
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => this.post(),
    });
  }

  private post(): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.startLoading();
    this.proxy
      .update(id, this.payload())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((saved) => {
        if (_.isNil(saved.data)) return;
        this.startLoading();
        this.proxy
          .post(id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe((response) => {
            if (_.isNil(response.data)) return;
            GlobalMessageUtil.success(this.title(), this.label('EmployeeAdvance_Posted', 'Advance posted.'));
            this.setAdvance(response.data);
          });
      });
  }

  confirmDelete(): void {
    const advance = this.advance();
    if (_.isNil(advance)) return;
    this.confirmationService.confirm({
      header: this.label('EmployeeAdvance_Delete_Title', 'Delete draft'),
      message: this.label('EmployeeAdvance_Delete_Message', 'Delete this draft advance and its attachments?'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.label('Delete', 'Delete'),
      rejectLabel: this.label('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.startLoading();
        this.proxy
          .delete(advance.id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe(() => {
            GlobalMessageUtil.success(this.title(), this.label('EmployeeAdvance_Deleted', 'Draft deleted.'));
            this.back();
          });
      },
    });
  }

  onFile(file: File): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.startLoading();
    this.proxy
      .addAttachment(id, file)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const updated = response.data;
        if (!_.isNil(updated)) this.advance.update((current) => (current ? { ...current, attachments: updated.attachments } : updated));
      });
  }

  openAttachment(url: string | null | undefined): void {
    if (url) window.open(url, '_blank');
  }

  openTrip(reference: string): void {
    this.router.navigate(['/trip-details'], { queryParams: { id: reference } });
  }

  back(): void {
    this.router.navigate([LIST_ROUTE]);
  }

  statusLabel(status: EmployeeAdvanceStatus): string {
    return cashDocumentStatusLabel(status, (k, f) => this.label(k, f));
  }

  statusSeverity(status: EmployeeAdvanceStatus): 'success' | 'warn' | 'secondary' {
    return cashDocumentStatusSeverity(status);
  }

  symbol(): string {
    return currencySymbol(this.advance()?.currency);
  }

  currencyCode(): string {
    return currencyLabel(this.advance()?.currency);
  }

  /** The bank is required to post only. */
  private validate(forPost: boolean): boolean {
    const bank = this.form.controls.bankAccount;
    bank.setValidators(forPost ? Validators.required : null);
    bank.updateValueAndValidity({ emitEvent: false });
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    return true;
  }

  /** The page holds one trip line; its amount is the form's. */
  private payload(): EmployeeAdvanceUpdate {
    const value = this.form.getRawValue();
    return {
      lines: (this.advance()?.lines ?? []).map((l, i) => ({ tripReference: l.trip.reference, amount: i === 0 ? value.amount : l.amount })),
      advanceDate: this.toApiDate(value.advanceDate ?? new Date()),
      bankAccountReference: value.bankAccount,
      bankingReference: value.bankingReference?.trim() || null,
      transferFee: value.bankingFee ?? 0,
      securityTax: value.securityFee ?? 0,
      note: value.note?.trim() || null,
    };
  }

  /** yyyy-MM-dd of the picked local day, so the server stores that calendar day. */
  private toApiDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  /** The stored calendar day as a local Date (the server keeps midnight UTC). */
  private fromApiDate(value: string | null): Date | null {
    if (_.isNil(value)) return null;
    const [year, month, day] = value.substring(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private title(): string {
    return this.label('EmployeeAdvance_Title', 'Employee advances');
  }

  private label(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
